import { useState, useEffect } from 'react'
import { observer } from 'mobx-react'
import { ApiErrorTypes } from 'ogram-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import check from '../../../../components/assets/check-icon-green.svg'
import cross from '../../../../components/assets/cross-icon-red.svg'
import ShiftCard from '../shift-card/shift-card'
import ShiftConfirmed from '../shift-confirmed/shift-confirmed'
import AttendanceCard, { AttendanceData } from '../attendance-card/attendance-card'
import { Job } from '../attendance-card/attendance-card'
import { useMutation, useStore } from '../../../../utils/mst-hooks'
import { AttendanceReport } from '../../../../models/attendance-report-job'
import { DAY_OFF_REASON_ID } from '../../../../models/shift-cancel-reason'
import { SpRatingPopup } from '../../../../components/sp-rating-popup/sp-rating-popup'
import { GetSPsToRateRequestProps } from '../../../../models/sp-to-rate'
import './shift-attendance-item.scss'

interface ShiftAttendanceItemProps {
  shift: AttendanceReport
  cancelReasonsLoaded: boolean
}

const ShiftAttendanceItem = observer(({ shift, cancelReasonsLoaded }: ShiftAttendanceItemProps) => {
  const { cancelReasons, updateAttendanceReportShift, getSPsToRate, spsToRateList, spsToRateNextPage } = useStore().data

  const [attendanceData, setAttendanceData] = useState<AttendanceData[]>([])
  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false)
  const [withRatingPopup, setWithRatingPopup] = useState(false)
  const [attendedCount, setAttendedCount] = useState(0)
  const [notAttendedCount, setNotAttendedCount] = useState(0)

  const [updateShiftAttendanceStatus, updateAttendance] = useMutation(updateAttendanceReportShift, {
    onSuccess: () => setShowConfirmationScreen(true),
    filterError: [ApiErrorTypes.ClientError],
  })

  const [getSPsToRateStatus, getSPsToRateRequest] = useMutation(
    ({ collect, page, jobId }: GetSPsToRateRequestProps) =>
      getSPsToRate({
        collect,
        jobId,
        page: page ?? spsToRateNextPage ?? 1,
      }),
    {
      onError: err => {
        toast.error(err.data.errors?.note[0])
      },
      filterError: [ApiErrorTypes.ClientError, ApiErrorTypes.NotFound],
    },
  )

  useEffect(() => {
    if (!cancelReasonsLoaded || !cancelReasons.length) {
      return
    }
    setAttendedCount(shift.active_shifts.filter(s => Boolean(s.client_checkin_time)).length)
    setNotAttendedCount(shift.active_shifts.filter(s => !s.client_checkin_time).length)
    setAttendanceData(initiateShiftAttendance())
  }, [cancelReasonsLoaded])

  useEffect(() => {
    if (shift.job_id && showConfirmationScreen) {
      getSPsToRateRequest({ collect: false, page: 1, jobId: shift.job_id })
    }
  }, [showConfirmationScreen])

  useEffect(() => {
    if (!showConfirmationScreen) return
    const unratedSPsCount = spsToRateList.filter(sp => sp.rating === null).length
    setWithRatingPopup(Boolean(unratedSPsCount))
  }, [spsToRateList, showConfirmationScreen])

  const { t } = useTranslation()

  function initiateShiftAttendance(): AttendanceData[] {
    return shift.active_shifts.map((activeShift): AttendanceData => {
      if (activeShift.client_checkin_time && activeShift.client_checkout_time) {
        return {
          id: activeShift.id,
          check_in_time: activeShift.client_checkin_time,
          check_out_time: activeShift.client_checkout_time,
          action: 'confirm',
        }
      }
      return {
        id: activeShift.id,
        action: 'cancel',
        reason: cancelReasons.find(reason => reason.id === DAY_OFF_REASON_ID)?.id,
      }
    })
  }

  const retrieveAttendanceData = (shiftAttendanceData: AttendanceData) => {
    const existedAttendanceData = attendanceData.find(data => data.id === shiftAttendanceData.id) as AttendanceData
    if (existedAttendanceData.action !== shiftAttendanceData.action) {
      if (shiftAttendanceData.action === 'cancel') {
        setAttendedCount(count => (count - 1 >= 0 ? count - 1 : 0))
        setNotAttendedCount(count => count + 1)
      } else if (shiftAttendanceData.action === 'confirm') {
        setAttendedCount(count => count + 1)
        setNotAttendedCount(count => (count - 1 >= 0 ? count - 1 : 0))
      }
    }

    setAttendanceData((data: AttendanceData[]) => {
      data[data.indexOf(existedAttendanceData)] = shiftAttendanceData
      return [...data]
    })
  }

  const confirmShift = () => updateAttendance({ shifts: attendanceData })

  if (showConfirmationScreen && !withRatingPopup) {
    return (
      <div className="ShiftAttendanceItem ShiftAttendanceItem--confirmed">
        <ShiftCard showLimitedTimeModal={true} shiftData={shift as Job} />
        <div className="ShiftAttendanceItem__confirmedInline">
          <ShiftConfirmed closeConfirmation={() => {}} />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="ShiftAttendanceItem">
        <div>
          <ShiftCard showLimitedTimeModal={true} shiftData={shift as Job} />
        </div>
        <div className="ShiftAttendanceItem__attendanceList">
          {shift.active_shifts.map(activeShift => (
            <AttendanceCard
              onUpdate={retrieveAttendanceData}
              key={activeShift.id}
              attendanceShift={activeShift}
              job={shift as Job}
            />
          ))}
        </div>
        <div className="ShiftAttendanceItem__confirm">
          <div className="ShiftAttendanceItem__attendanceCount">
            <div>
              <img src={check} />
              <strong>{attendedCount}</strong>
              <span> {t('shifts_attendance_details.attended')}</span>
            </div>
            <div>
              <img src={cross} />
              <strong>{notAttendedCount}</strong>
              <span> {t('shifts_attendance_details.did_not')}</span>
            </div>
          </div>
          {updateShiftAttendanceStatus.isLoading ? (
            <button disabled className="ShiftAttendanceItem__confirmButton">
              {t('shifts_attendance_details.confirm_amount', { count: shift.active_shifts.length })}
            </button>
          ) : (
            <button onClick={confirmShift} className="ShiftAttendanceItem__confirmButton">
              {t('shifts_attendance_details.confirm_amount', { count: shift.active_shifts.length })}
            </button>
          )}
        </div>
      </div>
      {Boolean(spsToRateList.length > 0) && (
        <SpRatingPopup
          jobId={shift.job_id}
          isActive={showConfirmationScreen && withRatingPopup}
          onClose={() => {
            setWithRatingPopup(false)
          }}
          getSPsToRateRequest={getSPsToRateRequest}
          spsToRateList={spsToRateList}
          spsToRateNextPage={spsToRateNextPage}
          isLoadingSPs={getSPsToRateStatus.isLoading}
        />
      )}
    </>
  )
})

export default ShiftAttendanceItem
